import axios from "axios";

const API_ADMIN = `${process.env.REACT_APP_API_URL}/api/admin`;
const API_GESTIONNAIRE = `${process.env.REACT_APP_API_URL}/api/gestionnaire`;

export default class ContratService {
  static getApiUrl(role) {
    return role === "admin" ? API_ADMIN : API_GESTIONNAIRE;
  }

  static async getContrats(role, token) {
    try {
      const url = `${this.getApiUrl(role)}/contrat`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const contratsData =
        response.data.contrats || response.data.resultats || response.data;

      if (!Array.isArray(contratsData)) {
        console.error("Les données reçues ne sont pas un tableau:", contratsData);
        throw new Error("Les données des contrats sont mal formatées.");
      }

      return contratsData;
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des contrats:",
        error.response?.data || error.message
      );
      throw new Error(
        error.response?.data?.message || "Erreur lors de la récupération des contrats."
      );
    }
  }

  static async getContratById(role, token, id) {
    try {
      const url = `${this.getApiUrl(role)}/contrat/${id}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération du contrat :", error.response?.data || error.message);
      throw error;
    }
  }

  static async createContrat(role, token, newContrat, onProgress) {
    const url = `${this.getApiUrl(role)}/contrat`;

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.setRequestHeader('X-User-Role', role);
      xhr.timeout = 0;

      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const percent = Math.round((e.loaded / e.total) * 100);
            onProgress(percent);
          }
        };
      }

      xhr.onload = () => {
        let data = {};
        try {
          data = JSON.parse(xhr.responseText);
        } catch (e) {
          console.error(
            `Réponse non-JSON reçue (status ${xhr.status}):`,
            xhr.responseText.substring(0, 500)
          );

          const status = xhr.status;
          let message;

          if (status === 413) {
            message = "Fichier trop volumineux. Augmentez upload_max_filesize dans php.ini et client_max_body_size dans Nginx.";
          } else if (status >= 200 && status < 300) {
            // ✅ 200 mais pas du JSON = session expirée, redirect, ou erreur PHP
            message = "Le serveur a renvoyé une réponse inattendue. Vérifiez les logs Laravel.";
          } else if (status === 401) {
            message = "Session expirée. Veuillez vous reconnecter.";
          } else if (status === 403) {
            message = "Accès non autorisé.";
          } else if (status === 422) {
            message = "Données invalides. Vérifiez les champs du formulaire.";
          } else if (status === 500) {
            message = "Erreur interne du serveur. Consultez les logs Laravel.";
          } else {
            message = `Erreur serveur (${status}). Le fichier est peut-être trop lourd.`;
          }

          return reject({
            response: { status, data: { message } }
          });
        }

        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(data);
        } else {
          // ✅ JSON reçu mais statut d'erreur (422 validation, 403, etc.)
          reject({ response: { status: xhr.status, data } });
        }
      };

      xhr.onerror = () => reject({
        response: {
          status: 0,
          data: { message: "Erreur réseau. Vérifiez votre connexion." }
        }
      });

      xhr.ontimeout = () => reject({
        response: {
          status: 0,
          data: { message: "Timeout : le fichier est trop long à envoyer." }
        }
      });

      xhr.send(newContrat);
    });
  }

  static async updateContrat(role, token, id, updatedContrat, onProgress) {
    const url = `${this.getApiUrl(role)}/contrat/${id}`;

    if (updatedContrat instanceof FormData) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.setRequestHeader('X-User-Role', role);
        xhr.timeout = 0;

        if (onProgress) {
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              const percent = Math.round((e.loaded / e.total) * 100);
              onProgress(percent);
            }
          };
        }

        xhr.onload = () => {
          // ✅ Même protection contre le HTML
          let data = {};
          try {
            data = JSON.parse(xhr.responseText);
          } catch {
            const status = xhr.status;
            if (status === 413) {
              return reject({
                response: {
                  status: 413,
                  data: { message: "Fichier trop volumineux. Augmentez upload_max_filesize dans php.ini et client_max_body_size dans Nginx." }
                }
              });
            }
            return reject({
              response: {
                status,
                data: { message: `Erreur serveur (${status}). Le fichier est peut-être trop lourd.` }
              }
            });
          }

          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(data);
          } else {
            reject({ response: { status: xhr.status, data } });
          }
        };

        xhr.onerror = () => reject({
          response: { status: 0, data: { message: "Erreur réseau. Vérifiez votre connexion." } }
        });

        xhr.send(updatedContrat);
      });
    }

    // Fallback axios pour PUT sans fichier
    const response = await axios.put(url, updatedContrat, {
      headers: { Authorization: `Bearer ${token}`, 'X-User-Role': role },
    });
    return response.data;
  }

  static async deleteContrat(role, token, id) {
    try {
      const url = `${this.getApiUrl(role)}/contrat/${id}`;
      const response = await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (error) {
      console.error(
        `Erreur lors de la suppression du contrat ${id}:`,
        error.response?.data || error.message
      );
      throw new Error(
        error.response?.data?.message || "Erreur lors de la suppression du contrat."
      );
    }
  }

  static async getMonthlyPayments(role, token, year) {
    try {
      const url = `${this.getApiUrl(role)}/contrat/monthly-payments`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        params: { year },
      });

      // Retourner les données telles quelles sans validation stricte
      return {
        contrats_events: response.data.contrats_events || [],
        monthly_payments_by_accounting_code:
          response.data.monthly_payments_by_accounting_code || [],
        totaux_generaux: response.data.totaux_generaux || {
          total_ht: 0,
          total_tva: 0,
        },
      };
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des paiements mensuels:",
        error.response?.data || error.message
      );
      throw new Error(
        error.response?.data?.message ||
        "Erreur lors de la récupération des paiements mensuels."
      );
    }
  }

  static async getComptesComptables(role, token, societeId) {
    try {
      const response = await axios.get(
        `${this.getApiUrl(role)}/comptes-comptables/by-societe`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { societe_id: societeId },
        }
      );
      return response.data;
    } catch (error) {
      console.error(
        "Erreur lors de la récupération du plan comptable",
        error.response?.data || error.message
      );
      throw error;
    }
  }


}